import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { findRegionByCode, getRegionName } from '../../utils/regions';
import { getRegionInfo } from '../../utils/regionInfo';
import './RegionsGamePage.css';

interface Region {
  region_code: number;
  status: 'active' | 'completed';
  completed_at?: string;
}

interface GameData {
  id: string;
  gameType: string;
  status: string;
  regions: Region[];
}

export function RegionsGamePage() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const [game, setGame] = useState<GameData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRegionCode, setSelectedRegionCode] = useState<number | null>(null);
  const regionsPerPage = 50;

  useEffect(() => {
    loadGame();
    
    // Сохраняем текущую игру как последнюю
    if (gameId) {
      localStorage.setItem('last-game', JSON.stringify({ gameId, gameType: 'regions' }));
      
      // Восстанавливаем сохраненную страницу для этой игры
      const savedPage = localStorage.getItem(`regions-game-page-${gameId}`);
      if (savedPage) {
        const page = parseInt(savedPage, 10);
        if (page >= 1) {
          setCurrentPage(page);
        }
      }
    }
  }, [gameId]);

  const loadGame = async () => {
    try {
      const response = await api.get(`/games/${gameId}`);
      const gameData = response.data;
      
      // Убеждаемся, что регионы загружены и отсортированы
      if (gameData.regions && Array.isArray(gameData.regions)) {
        // Сортируем регионы по коду для правильного отображения
        gameData.regions.sort((a: Region, b: Region) => a.region_code - b.region_code);
      } else {
        console.error('No regions array in game data:', gameData);
      }
      
      setGame(gameData);
    } catch (error) {
      console.error('Failed to load game:', error);
      alert('Ошибка загрузки игры');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleRegionClick = (regionCode: number) => {
    if (game?.regions.find((r) => r.region_code === regionCode)?.status === 'active') {
      setSelectedRegionCode(regionCode);
    }
  };

  const handleComplete = async () => {
    if (!selectedRegionCode) return;

    try {
      await api.post(`/games/${gameId}/regions/complete`, { regionCode: selectedRegionCode });
      setSelectedRegionCode(null);
      await loadGame();
    } catch (error: any) {
      // Обрабатываем офлайн-режим
      if (error.response?.data?.offline || error.isOffline) {
        alert('Запрос сохранен. Будет отправлен при восстановлении соединения.');
        // Обновляем локально для отображения
        if (game) {
          const updatedRegions = game.regions.map((region) =>
            region.region_code === selectedRegionCode
              ? { ...region, status: 'completed' as const, completed_at: new Date().toISOString() }
              : region
          );
          setGame({
            ...game,
            regions: updatedRegions,
          });
        }
        setSelectedRegionCode(null);
      } else {
        alert(error.response?.data?.error?.message || 'Ошибка');
      }
    }
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  if (!game) {
    return <div>Игра не найдена</div>;
  }

  const regions = game.regions || [];
  const totalPages = Math.ceil(regions.length / regionsPerPage);
  const startIndex = (currentPage - 1) * regionsPerPage;
  const endIndex = startIndex + regionsPerPage;
  const currentRegions = regions.slice(startIndex, endIndex);

  const completedCount = regions.filter((r) => r.status === 'completed').length;
  const totalCount = regions.length;

  return (
    <div className="regions-game-page">
      <div className="game-header">
        <button className="back-button" onClick={() => {
          // Устанавливаем флаг, что пользователь явно вышел на главную
          localStorage.setItem('skip-auto-redirect', 'true');
          navigate('/');
        }}>
          ← Назад
        </button>
        <h1>Регионы</h1>
        <div className="progress">
          {completedCount} / {totalCount}
        </div>
      </div>

      <div className="regions-grid">
        {currentRegions.map((region) => {
          const regionInfo = findRegionByCode(region.region_code);
          return (
            <button
              key={region.region_code}
              className={`region-button ${
                region.status === 'completed' ? 'completed' : 'active'
              }`}
              onClick={() => handleRegionClick(region.region_code)}
              title={regionInfo?.name || `Регион ${region.region_code}`}
            >
              <div className="region-code">
                {String(region.region_code).padStart(2, '0')}
              </div>
              <div className="region-name">
                {regionInfo ? regionInfo.name.split(' ').slice(0, 2).join(' ') : `Регион ${region.region_code}`}
              </div>
            </button>
          );
        })}
      </div>

      <div className="pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => {
            const newPage = currentPage - 1;
            setCurrentPage(newPage);
            if (gameId) {
              localStorage.setItem(`regions-game-page-${gameId}`, String(newPage));
            }
          }}
        >
          Предыдущая
        </button>
        <span>
          Страница {currentPage} из {totalPages}
        </span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => {
            const newPage = currentPage + 1;
            setCurrentPage(newPage);
            if (gameId) {
              localStorage.setItem(`regions-game-page-${gameId}`, String(newPage));
            }
          }}
        >
          Следующая
        </button>
      </div>

      {selectedRegionCode !== null && (
        <div className="modal-overlay" onClick={() => setSelectedRegionCode(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Погасить регион {String(selectedRegionCode).padStart(2, '0')}?</h2>
            <p className="region-name-modal">{getRegionName(selectedRegionCode)}</p>
            {getRegionInfo(selectedRegionCode) && (
              <div className="region-info">
                <h3>Интересная информация:</h3>
                <p className="region-info-text">{getRegionInfo(selectedRegionCode)}</p>
              </div>
            )}
            <div className="modal-actions">
              <button className="confirm-button" onClick={handleComplete}>
                Погасить
              </button>
              <button
                className="cancel-button"
                onClick={() => setSelectedRegionCode(null)}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

